import * as React from 'react';
import { Field } from 'react-final-form';
import { Primitive } from 'types/Primitives';

export enum ConditionOperator {
	EqualTo,
	NotEqualTo,
	GreaterThan,
	LessThan,
	GreaterThanOrEqualTo,
	LessThanOrEqualTo,
	Contains,
}

interface ConditionProps {
	/** The name of the field in the form to check */
	when: string;

	/**
	 * The logical operator to use when comparing the the value of the "when" field to the passed in value. If the resulting
	 * conditional expression is true, the children will be rendered. Operator and value are required if condition is not used.
	 */
	operator?: ConditionOperator;

	/**
	 * A conditional function that takes the value of the field with the name in the "when" argument and returns true if the children
	 * of the Condition component should be rendered. Required if operator or value are omitted.
	 */
	condition?: (value: any) => boolean;

	/**
	 * The value to compare the value of the "when" field to using the designated operator. If the resulting conditional expression
	 * is true, the children will be rendered. Operator and value are required if condition is not used.
	 */
	value?: Primitive | Primitive[];

	/** The children of the Condition component */
	children: React.ReactNode;
}

/**
 * Conditionally renders the components passed in as children, based on form values.
 */
const Condition = (props: ConditionProps) => {
	const { when, operator, condition, children } = props;
	const arrayValue: Primitive[] | null = Array.isArray(props.value) ? props.value : null;

	return (
		<Field name={when} subscription={{ value: true }}>
			{({ input: { value } }) => {
				if (condition) {
					return condition(value) ? children : null;
				}

				if (props.value === undefined) {
					return null;
				}

				switch (operator) {
					case ConditionOperator.EqualTo:
					default:
						return arrayValue
							? arrayValue.includes(value)
								? children
								: null
							: value === props.value
							? children
							: null;

					case ConditionOperator.NotEqualTo:
						return arrayValue
							? !arrayValue.includes(value)
								? children
								: null
							: value !== props.value
							? children
							: null;

					case ConditionOperator.GreaterThan:
						return value > props.value ? children : null;

					case ConditionOperator.GreaterThanOrEqualTo:
						return value >= props.value ? children : null;

					case ConditionOperator.LessThan:
						return value < props.value ? children : null;

					case ConditionOperator.LessThanOrEqualTo:
						return value <= props.value ? children : null;

					case ConditionOperator.Contains:
						return value.toString().toLocaleLowerCase().includes(props.value.toString().toLocaleLowerCase())
							? children
							: null;
				}
			}}
		</Field>
	);
};

export default Condition;
